import React, { useEffect, useMemo, useState } from 'react';
import { StandoutDropdown } from '../../../components/StandoutDropdown';
import { Alert, Button, Form, Modal, Spinner } from '../../../components/ui';
import { areasService } from '../../../services/areasService';
import { locationsService } from '../../../services/locationsService';
import type { SiteWithLocations } from '../types';
import {
  DESCRIPTION_MAX_LENGTH,
  OWNER_CHOICES,
  siteFormFrom,
  toSiteInput,
  validateSiteForm,
  type SiteForm,
} from '../utils/structureForms';
import type { AreaOption, ForemanAreaOption } from './LocationsFilters';

interface SiteFormModalProps {
  show: boolean;
  /** The site to edit, or null to add a new one. */
  site: SiteWithLocations | null;
  onHide: () => void;
  /** After the server saved it, with the site as it now is. */
  onSaved: (site: SiteWithLocations, message: string) => void;
}

const Required: React.FC = () => (
  <span className="tw-text-danger" aria-hidden="true">
    {' '}
    *
  </span>
);

/**
 * Adds a pit or yard, or edits one: name, number, owner, where it sits (service area and the
 * foreman areas whose crews draw from it), its position on the map and a description.
 * Storage locations and bins are added afterwards, from the site window.
 */
const SiteFormModal: React.FC<SiteFormModalProps> = ({ show, site, onHide, onSaved }) => {
  const [form, setForm] = useState<SiteForm>(() => siteFormFrom(site));
  const [attempted, setAttempted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [serviceAreas, setServiceAreas] = useState<AreaOption[]>([]);
  const [foremanAreas, setForemanAreas] = useState<ForemanAreaOption[]>([]);

  // Open fresh each time, from the site being edited or blank.
  useEffect(() => {
    if (!show) return;
    setForm(siteFormFrom(site));
    setAttempted(false);
    setSaving(false);
    setSaveError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  // The server only lists the areas the caller works in, which are the ones a site can go in.
  useEffect(() => {
    if (!show) return undefined;
    let cancelled = false;
    Promise.all([areasService.getServiceAreas(), areasService.getForemanAreas()])
      .then(([services, foremen]) => {
        if (cancelled) return;
        setServiceAreas(services.map((area) => ({ id: String(area.id), name: area.name })));
        setForemanAreas(foremen.map((area) => ({ id: String(area.id), name: area.name, serviceAreaId: String(area.serviceAreaId) })));
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [show]);

  const foremanOptions = useMemo(
    () => foremanAreas.filter((area) => area.serviceAreaId === form.serviceAreaId).sort((a, b) => a.name.localeCompare(b.name)),
    [foremanAreas, form.serviceAreaId]
  );

  const errors = validateSiteForm(form);
  const shown = attempted ? errors : {};
  const set = <K extends keyof SiteForm>(key: K, value: SiteForm[K]) => {
    setForm((current) => ({ ...current, [key]: value }));
    setSaveError(null);
  };

  const save = async () => {
    setAttempted(true);
    if (Object.keys(errors).length) return;
    setSaving(true);
    setSaveError(null);
    try {
      const input = toSiteInput(form);
      const saved = site ? await locationsService.updateSite(site.id, input) : await locationsService.createSite(input);
      onSaved(saved, site ? `Saved ${saved.name}.` : `Added ${saved.name}. Add its storage locations and bins next.`);
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : 'Failed to save the site');
      setSaving(false);
    }
  };

  const text = (key: 'name' | 'number' | 'latitude' | 'longitude', label: React.ReactNode, props: Record<string, unknown> = {}) => (
    <Form.Group controlId={`site-form-${key}`}>
      <Form.Label>{label}</Form.Label>
      <Form.Control
        value={form[key]}
        isInvalid={!!shown[key]}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => set(key, event.target.value)}
        {...props}
      />
      {shown[key] ? <Form.Control.Feedback type="invalid">{shown[key]}</Form.Control.Feedback> : null}
    </Form.Group>
  );

  return (
    <Modal show={show} onHide={saving ? () => undefined : onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{site ? `Edit ${site.name}` : 'Add a site'}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="tw-space-y-4">
        {saveError ? <Alert variant="danger" className="tw-mb-0">{saveError}</Alert> : null}

        <div className="tw-grid tw-grid-cols-1 tw-gap-3 sm:tw-grid-cols-3">
          <div className="sm:tw-col-span-2">
            {text(
              'name',
              <>
                Name
                <Required />
              </>,
              { autoFocus: true, placeholder: 'e.g. Lac la Hache Pit' }
            )}
          </div>
          {text('number', 'Site number', { placeholder: 'e.g. 731' })}
        </div>

        <div className="tw-grid tw-grid-cols-1 tw-gap-3 sm:tw-grid-cols-2">
          <Form.Group controlId="site-form-type">
            <Form.Label>
              Type
              <Required />
            </Form.Label>
            <Form.Select
              value={form.type}
              onChange={(event: React.ChangeEvent<HTMLSelectElement>) => set('type', event.target.value as SiteForm['type'])}
            >
              <option value="yard">Yard</option>
              <option value="pit">Pit</option>
            </Form.Select>
          </Form.Group>
          <Form.Group controlId="site-form-owner">
            <Form.Label>Owner</Form.Label>
            <Form.Select value={form.owner} onChange={(event: React.ChangeEvent<HTMLSelectElement>) => set('owner', event.target.value)}>
              <option value="">Not recorded</option>
              {OWNER_CHOICES.map((owner) => (
                <option key={owner} value={owner}>
                  {owner}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </div>

        <div className="tw-grid tw-grid-cols-1 tw-gap-3 sm:tw-grid-cols-2">
          <div>
            <StandoutDropdown
              type="searchable"
              label="Service area *"
              options={serviceAreas}
              value={form.serviceAreaId}
              // Foreman areas belong to one service area, so a new one starts the list again.
              onChange={(value) => setForm((current) => ({ ...current, serviceAreaId: value || '', foremanAreaIds: value === current.serviceAreaId ? current.foremanAreaIds : [] }))}
              getValue={(area: AreaOption) => area.id}
              formatOption={(area: AreaOption) => area.name}
              placeholder="Choose a service area"
            />
            {shown.serviceAreaId ? <p className="tw-mb-0 tw-mt-1 tw-text-sm tw-text-danger">{shown.serviceAreaId}</p> : null}
          </div>
          <div>
            <StandoutDropdown
              type="multiselect"
              label="Foreman areas"
              options={foremanOptions}
              value={form.foremanAreaIds}
              onChange={(values) => set('foremanAreaIds', values.map(String))}
              getValue={(area: ForemanAreaOption) => area.id}
              formatOption={(area: ForemanAreaOption) => area.name}
              placeholder={form.serviceAreaId ? 'The crews that draw from it' : 'Choose a service area first'}
              disabled={!form.serviceAreaId}
              noOptionsMessage="No foreman areas in that service area"
            />
            <p className="tw-mb-0 tw-mt-1 tw-text-xs tw-text-content-muted">Decides which crews see this site in the field app.</p>
          </div>
        </div>

        <div className="tw-grid tw-grid-cols-1 tw-gap-3 sm:tw-grid-cols-2">
          {text('latitude', 'Latitude', { inputMode: 'decimal', placeholder: 'e.g. 51.6431' })}
          {text('longitude', 'Longitude', { inputMode: 'decimal', placeholder: 'e.g. -121.2958' })}
        </div>

        <Form.Group controlId="site-form-description">
          <Form.Label>Description</Form.Label>
          <Form.Control
            as="textarea"
            rows={2}
            maxLength={DESCRIPTION_MAX_LENGTH}
            value={form.description}
            placeholder="Address or directions"
            isInvalid={!!shown.description}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => set('description', event.target.value)}
          />
          {shown.description ? <Form.Control.Feedback type="invalid">{shown.description}</Form.Control.Feedback> : null}
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide} disabled={saving}>
          Cancel
        </Button>
        <Button variant="primary" onClick={() => void save()} disabled={saving}>
          {saving ? <Spinner animation="border" size="sm" /> : null}
          {site ? 'Save site' : 'Add site'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default SiteFormModal;
